import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { SEO } from '@/components/SEO';
import api from '@/lib/api';

export default function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [status, setStatus] = useState('verifying'); // 'verifying', 'success', 'error'
  const [message, setMessage] = useState('');

  useEffect(() => {
    const verifyEmail = async () => {
      if (!token) {
        setStatus('error');
        setMessage('Invalid verification link. No token provided.');
        return;
      }
      
      try {
        const { data } = await api.post('/auth/verify-email', null, { params: { token } });
        setStatus('success');
        setMessage(data.message || 'Email verified successfully!');
      } catch (err) {
        setStatus('error');
        const errorDetail = err.response?.data?.detail;
        setMessage(typeof errorDetail === 'string' ? errorDetail : 'Verification failed. Please try again.');
      }
    };
    
    verifyEmail();
  }, [token]);

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-16" data-testid="verify-email-page">
      <SEO 
        title="Email Verification"
        description="Verify your email address for Twenty Tulips Diaries."
        pathname="/verify-email"
        noindex={true}
      />
      <div className="max-w-md w-full text-center">
        {status === 'verifying' && (
          <>
            <div className="mx-auto w-16 h-16 bg-stone-100 rounded-full flex items-center justify-center mb-6">
              <Loader2 className="h-8 w-8 text-stone-600 animate-spin" />
            </div>
            <h1 className="text-2xl md:text-3xl font-medium mb-4">Verifying your email...</h1>
            <p className="text-stone-600">Please wait while we verify your email address.</p>
          </>
        )}
        
        {status === 'success' && (
          <>
            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-6">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <h1 className="text-2xl md:text-3xl font-medium mb-4">Email Verified!</h1>
            <p className="text-stone-600 mb-8">{message}</p>
            <Link to="/login">
              <Button className="btn-primary" data-testid="continue-to-login-button">
                Continue to Sign In
              </Button>
            </Link>
          </>
        )}
        
        {status === 'error' && (
          <>
            <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-6">
              <XCircle className="h-8 w-8 text-red-600" />
            </div>
            <h1 className="text-2xl md:text-3xl font-medium mb-4">Verification Failed</h1>
            <p className="text-stone-600 mb-8">{message}</p>
            <div className="space-y-4">
              <Link to="/login">
                <Button className="btn-primary w-full">
                  Go to Sign In
                </Button>
              </Link>
              <p className="text-sm text-stone-500">
                Need a new verification link?{' '}
                <Link to="/login" className="underline hover:text-stone-700">
                  Sign in to request one
                </Link>
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
